<template>
  <div>
    <div class="text-xl font-bold">Profile</div>
    <div class="mt-12">
      <img :src="form.avatar" v-if="form.avatar" class="object-cover w-20 h-20 rounded-full" />
      <svg v-else width="80" height="80" xmlns="http://www.w3.org/2000/svg">
        <g fill-rule="nonzero" fill="none">
          <circle fill="#D1D6D4" cx="40" cy="39.771" r="39.114" />
          <path
            d="M54.286 35.629V29.2a14.286 14.286 0 00-7.2-12.486 14.286 14.286 0 00-14.286 0A14.286 14.286 0 0025.714 29.2v6.429A12.629 12.629 0 0032.03 46.57a16.2 16.2 0 0016.171 0 12.629 12.629 0 006.086-10.942z"
            fill="#F3F7F5"
          />
          <path
            d="M40 78.857a38.971 38.971 0 0026.914-10.771 31.429 31.429 0 00-53.828 0A38.971 38.971 0 0040 78.857z"
            fill="#F3F7F5"
          />
        </g>
      </svg>
    </div>
    <div class="mt-8 font-medium text-active cursor-pointer">
      <span v-if="uploadingAvatar">Processing...</span>
      <span v-else @click="$refs.avatar.click()">Change Photo</span>
    </div>
    <form ref="avatar-upload-form">
      <input type="file" ref="avatar" @change="uploadFile" class="hidden" />
    </form>
    <form class="w-full mt-8 lg:w-2/4" @submit.prevent="updateProfile">
      <div class="flex flex-wrap -mx-3">
        <div class="w-full px-3 mb-6 md:w-1/2 md:mb-0">
          <label class="text-xs font-medium" for="first-name">First name</label>
          <input
            v-model="$v.form.fname.$model"
            class="w-full h-12 px-3 py-4 text-xs font-medium outline-none placeholder-label-type-1 bg-gray-type-6 focus:outline-none"
            type="text"
            id="first-name"
          />
          <span
            class="text-xs font-bold text-red-400"
            v-if="!$v.form.fname.required && $v.form.fname.$error"
          >Please enter your first name</span>
        </div>
        <div class="w-full px-3 md:w-1/2">
          <label class="text-xs font-medium" for="last-name">Last name</label>
          <input
            v-model="$v.form.lname.$model"
            class="w-full h-12 px-3 py-4 text-xs font-medium outline-none placeholder-label-type-1 bg-gray-type-6 focus:outline-none"
            type="text"
            id="last-name"
          />
          <span
            class="text-xs font-bold text-red-400"
            v-if="!$v.form.lname.required && $v.form.lname.$error"
          >Please enter your last name</span>
        </div>
      </div>
      <div class="mt-6">
        <label class="text-xs font-medium" for="email-address">Email address</label>
        <div class="mt-2">
          <div
            id="email-address"
            class="pointer-events-none w-full h-12 px-3 py-4 text-xs font-medium outline-none bg-gray-type-6 text-label"
          ><span>{{form.email}}</span></div>
        </div>
      </div>
      <div class="mt-6">
        <label class="text-xs font-medium" for="phone">Phone number</label>
        <div class="mt-2">
          <input
            v-model="$v.form.phone.$model"
            type="text"
            id="phone"
            class="w-full h-12 px-3 py-4 text-xs font-medium outline-none placeholder-label-type-1 bg-gray-type-6 focus:outline-none"
            placeholder="0706 111 1198"
          />
          <div
            class="text-xs font-bold text-red-400"
            v-if="!$v.form.phone.required && $v.form.phone.$error"
          >Please enter a valid phone number</div>
          <div
            class="text-xs font-bold text-red-400"
            v-if="$v.form.phone.phoneNumber && !$v.form.phone.minLength"
          >Phone number must be at least {{$v.form.phone.$params.minLength.min}} digits</div>
        </div>
      </div>

      <div class="mt-6">
        <label class="text-xs font-medium" for="city">Your City</label>
        <div class="mt-2">
          <select class="w-full h-12 px-3 py-4 text-xs font-medium outline-none placeholder-label-type-1 bg-gray-type-6 focus:outline-none" v-model="$v.form.city_id.$model" name="city"  id="city">
            <option :value="s.city.id" v-for="s in supportedCities" :key="s.id">{{  s.city.name }}</option>
          </select>
          <div
            class="text-xs font-bold text-red-400"
            v-if="!$v.form.city_id.required && $v.form.city_id.$error"
          >Please select a city</div>
        </div>
      </div>


      <div class="flex flex-wrap mt-6 -mx-3">
        <div class="w-full px-3 mb-6 md:w-1/2 md:mb-0">
          <label class="text-xs font-medium" for="gender">Gender</label>
          <select
            v-model="form.gender"
            class="w-full h-12 px-3 py-4 text-xs font-medium outline-none placeholder-label-type-1 bg-gray-type-6 focus:outline-none"
            id="gender"
          >
            <option value disabled>Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
        <div class="w-full px-3 md:w-1/2">
          <label class="text-xs font-medium" for="date_of_birth">Date of Birth</label>
          <flat-pickr
            v-model="$v.form.dob.$model"
            placeholder="Date of birth"
            :config="{ maxDate: 'today' }"
            class="w-full h-12 px-3 py-4 text-xs font-medium outline-none placeholder-label-type-1 bg-gray-type-6 focus:outline-none"
            id="date_of_birth"
          ></flat-pickr>
          <div
            class="text-xs font-bold text-red-400"
            v-if="!$v.form.dob.required && $v.form.dob.$error"
          >Please select your date of birth</div>
        </div>
      </div>
      <button
        type="submit"
        class="block w-full h-12 py-4 mt-12 font-bold text-center text-white rounded focus:outline-none"
        :class="processing ? 'bg-gray-type-7' : 'bg-active'"
        :dislabed="processing"
      >{{ processing ? 'Processing...' : 'Save Changes' }}</button>
    </form>
  </div>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
import flatPickr from "vue-flatpickr-component";
import { required, minLength } from "vuelidate/lib/validators";

const phoneNumber = value => {
  return /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/.test(
    value
  );
};

export default {
  components: {
    flatPickr
  },
  data() {
    return {
      form: {},
      processing: false,
      errorMessage: "",
      uploadingAvatar: false
    };
  },
  validations: {
    form: {
      email: {
        required
      },
      fname: {
        required
      },
      lname: {
        required
      },
      city_id: {
        required
      },
      phone: {
        required,
        phoneNumber,
        minLength: minLength(11)
      },
      dob: {
        required
      }
    }
  },
  watch: {
    user() {
      this.form = { ...this.user };
    }
  },
  mounted() {
    this.form = { ...this.user };
  },
  created() {
    if(this.user && this.user.city_id) {
      this.form.city_id = this.user.city_id;
    }
    if (!this.supportedCities.length) {
      this.$store.dispatch("app/fetchSupportedCities");
    }
  },
  computed: {
    ...mapGetters("app", ["supportedCities"]),
    ...mapGetters("auth", ["user"])
  },
  methods: {
    updateProfile() {
      this.$v.form.$touch();

      if (this.processing || this.$v.form.$error) {
        return;
      }

      this.processing = true;

      this.$store
        .dispatch(
          "auth/updateUser",
          _.pick(this.form, [
            "fname",
            "lname",
            "email",
            "phone",
            "gender",
            "dob",
            "city_id"
          ])
        )
        .then(() => {
          this.$toast.success("Profile updated successfully.");
        })
        .catch(() => {
          this.$toast.error("Profile updated failed, please try again.");
        })
        .finally(() => (this.processing = false));
    },
    uploadFile() {
      this.file = this.$refs.avatar.files[0];

      const reader = new FileReader();

      reader.addEventListener("load", fileLoadedEvent => {
        const avatar = fileLoadedEvent.target.result;

        this.uploadingAvatar = true;

        this.$store
          .dispatch("auth/updateAvatar", { avatar })
          .then(() => {
            this.$toast.success("Avatar updated successfully.");
          })
          .catch(() => {
            this.$toast.error("Avatar update failed, please try again.");
          })
          .finally(() => (this.uploadingAvatar = false));
      });

      reader.readAsDataURL(this.file);
    }
  }
};
</script>